import React, { memo, useMemo, useRef, useEffect, useState } from "react";
import { Modal, ModalBody } from "reactstrap";
import { toast } from "react-toastify";
import { useAppointmentStore } from "../../store/useAppointmentStore"; 
import { useChatStore } from "../../store/useChatStore";
import { API_URLS } from '../../constants/apiUrls';
import { useAuthStore } from "../../store/useAuthStore";
import { APPOINTMENT_STATUS } from "../../constants/Messages";
import { TOAST_APPOINTMENT_CANCELED, TOAST_APPOINTMENT_ACCEPTED, TOAST_APPOINTMENT_REJECTED } from "../../constants/SearchFilter";
import { getShortTimezone, isAppointmentExpired } from "../../utils/chatUtils";
import { buildAppointmentHostContract } from "../../utils/appointmentHostContract";
import { useAppSettings } from "../../context/AppSettingsContext";

const AppointmentPopupSummaryComponent = () => { 
    const chatClient = useChatStore((state) => state.chatClient);
    const appointmentSummaryDetails = useAppointmentStore((state) => state.appointmentSummaryDetails); 
    const isAppointmentSummaryPopupOpen = useAppointmentStore((state) => state.isAppointmentSummaryPopupOpen);
    const setIsAppointmentSummaryPopupOpen = useAppointmentStore((state) => state.setIsAppointmentSummaryPopupOpen);  
    const iframeRef = useRef(null);
    const [isIframeLoading, setIsIframeLoading] = useState(true);
    const hasDataBeenSentRef = useRef(false);
    const isFlutterReadyRef = useRef(false);

    const { authData } = useAuthStore.getState();
    const { eventTimezone } = useAppSettings();

    // Flutter iframe configuration
    const flutterIframeUrl = useMemo(() => {
        const baseUrl = API_URLS.APP_VIEW_APPOINTMENT_IFRAME_URL;
        return `${baseUrl}`;
    }, []);
    
    const flutterOrigin = API_URLS.APP_FLUTTER_ORIGIN;

    // ✅ REMOVED: Session storage caching (doesn't work cross-origin)
    // React and Flutter iframe have separate session storage
    // Flutter will cache data after receiving it via postMessage

    // Reset state when modal opens
    useEffect(() => {
        if (isAppointmentSummaryPopupOpen) {
            hasDataBeenSentRef.current = false;
            isFlutterReadyRef.current = false;
            setIsIframeLoading(true);
        }
    }, [isAppointmentSummaryPopupOpen]);

    // Listen for appointment cancellation, accept or deny via message.updated event
    useEffect(() => {
        if (!chatClient || !isAppointmentSummaryPopupOpen || !appointmentSummaryDetails?.messageId) {
            return;
        }

        const handleMessageUpdated = (event) => {
            // Check if the updated message is the one currently being viewed
            if (event?.message?.id !== appointmentSummaryDetails?.messageId) {
                return;
            }

            // Ignore updates this user made from the open iframe. Closing here would unmount
            // Flutter before it sends the "I have canceled ..." channel message.
            if (event?.user?.id && event.user.id === chatClient.userID) {
                return;
            }

            const updatedStatus = event?.message?.appointment?.status;
            const currentStatus = appointmentSummaryDetails?.status;
            const isMyMessage = appointmentSummaryDetails?.isMyMessage;

            // Skip if status hasn't changed
            if (updatedStatus === currentStatus) {
                return;
            }

            // Define status change notifications
            const statusNotifications = {
                [APPOINTMENT_STATUS.ACCEPTED]: {
                    condition: isMyMessage,
                    toastFn: () => toast.success(TOAST_APPOINTMENT_ACCEPTED, { toastId: "appointment-accepted" })
                },
                [APPOINTMENT_STATUS.REJECTED]: {
                    condition: isMyMessage,
                    toastFn: () => toast.info(TOAST_APPOINTMENT_REJECTED, { toastId: "appointment-rejected" })
                },
                [APPOINTMENT_STATUS.CANCELED]: {
                    // Either participant can cancel an accepted appointment
                    condition: true,
                    toastFn: () => toast.info(TOAST_APPOINTMENT_CANCELED, { toastId: "appointment-canceled" })
                }
            };

            const notification = statusNotifications[updatedStatus];
            if (notification?.condition) {
                notification.toastFn();
                setIsAppointmentSummaryPopupOpen(false);
            }
        };

        chatClient.on("message.updated", handleMessageUpdated);

        return () => {
            chatClient.off("message.updated", handleMessageUpdated);
        };
    }, [chatClient, isAppointmentSummaryPopupOpen, appointmentSummaryDetails?.messageId, appointmentSummaryDetails?.status, appointmentSummaryDetails?.isMyMessage, setIsAppointmentSummaryPopupOpen]);

    // Close handler
    const handleClose = () => {
        // ✅ No session storage cleanup needed in React
        // Flutter iframe handles its own session storage
        setIsAppointmentSummaryPopupOpen(false);
    };

    // ✅ Send postMessage to trigger WebNativeBridge listener
    useEffect(() => {
        if(!isAppointmentSummaryPopupOpen || !appointmentSummaryDetails) {
            return;
        }

        let fallbackTimerId = null;
        let initialSendTimerId = null;

        const sendDataToFlutter = () => {
            if (hasDataBeenSentRef.current) {
                console.log('[React] Data already sent to Flutter, skipping...');
                return;
            }

            if (!iframeRef.current?.contentWindow) {
                console.warn('[React] Iframe contentWindow not available');
                return;
            }

            const message = {
                type: 'appointment_summary_init_data',
                fromNative: false,                          
                appointment: {
                    messageId: appointmentSummaryDetails?.messageId || null,
                    channelId: appointmentSummaryDetails?.channelId || null,
                    isMyMessage: appointmentSummaryDetails.isMyMessage,
                    isExpired: isAppointmentExpired(
                        appointmentSummaryDetails?.dateString,
                        appointmentSummaryDetails?.startTime,
                        appointmentSummaryDetails?.endTime,
                        eventTimezone
                    ),
                    targetId: appointmentSummaryDetails?.targetId,
                    date: appointmentSummaryDetails?.date || null,
                    startTime: appointmentSummaryDetails?.startTime || null,
                    endTime: appointmentSummaryDetails?.endTime || null,
                    location: appointmentSummaryDetails?.location || null,
                    note: appointmentSummaryDetails?.note || null,                
                    totalMinutes: appointmentSummaryDetails?.totalMinutes || 0,
                    status: appointmentSummaryDetails?.status || 0,
                    name: appointmentSummaryDetails?.name || null,
                    title: appointmentSummaryDetails?.title || null,
                    jwtToken: authData?.accessToken || null,
                    ...buildAppointmentHostContract(), // streamKey, environment, isGatewayEnable
                    timezone: getShortTimezone(eventTimezone), // Event timezone abbreviation for Flutter display, forward compatible with future timezone-based features in Flutter
                },
                timestamp: new Date().toISOString()
            };

            try {
                iframeRef.current.contentWindow.postMessage(
                    JSON.stringify(message),
                    flutterOrigin
                );

                hasDataBeenSentRef.current = true;
                console.log('[React] ✅ Sent appointment data via postMessage');
                
                // Start fallback timer after sending data
                if (fallbackTimerId) {
                    clearTimeout(fallbackTimerId);
                }
                fallbackTimerId = setTimeout(() => {
                    console.warn('[React] ⏱️ Flutter confirmation timeout, hiding spinner');
                    setIsIframeLoading(false);
                }, 5000);
                
            } catch (error) {
                console.error('[React] ❌ Error sending message to Flutter iframe:', {
                    error: error.message,
                    stack: error.stack,
                    message: message
                });
                setIsIframeLoading(false);
            }
        };

        const handleFlutterMessage = (event) => {
            // Security check (uncomment for production)
            // if (event.origin !== flutterOrigin) {
            //     console.warn('[React] Message from untrusted origin:', event.origin);
            //     return;
            // }

            try {
                if (!event.data) {
                    return;
                }

                const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

                console.log('[React] Received Flutter message:', data.type); // Debug log

                if (data.type === 'flutter_api_request') {
                    console.log('[React] Flutter API Request:', {
                        method: data.method,
                        endpoint: data.endpoint
                    });
                }

                if (data?.type === 'flutter_ready') {
                    console.log('[React] ✅ Flutter is ready - sending appointment data');
                    isFlutterReadyRef.current = true;
                    sendDataToFlutter();
                }

                // Handle any of these messages to hide spinner
                const dataLoadedMessages = [
                    data?.type === 'data_loaded',
                    data?.type === 'appointment_action' && data?.action === 'data_loaded',
                    data?.type === 'flutter_data_received', // Also hide on acknowledgment
                    data?.type === 'content_dimensions' // Hide when dimensions are received (content rendered)
                ];

                if (dataLoadedMessages.some(condition => condition)) {
                    console.log('[React] ✅ Flutter confirmed ready state:', data.type);
                    
                    // Clear fallback timer
                    if (fallbackTimerId) {
                        clearTimeout(fallbackTimerId);
                        fallbackTimerId = null;
                    }
                    
                    setIsIframeLoading(false);
                }

                // Handle load failures
                if (data?.type === 'data_load_failure' || 
                    (data?.type === 'appointment_action' && data?.action === 'data_load_failure')) {
                    console.error('[React] ❌ Flutter failed to load data:', data.error);
                    if (fallbackTimerId) {
                        clearTimeout(fallbackTimerId);
                    }
                    setIsIframeLoading(false);
                }

                // Handle content dimensions
                if (data?.type === 'content_dimensions' && data?.width && data?.height) {
                    const viewportWidth = window.innerWidth;
                    const viewportHeight = window.innerHeight;
                    
                    const maxWidth = Math.min(viewportWidth * 0.9, data.width + 32);
                    const maxHeight = Math.min(viewportHeight * 0.9, data.height + 32);
                    
                    const finalWidth = Math.max(300, maxWidth);
                    const finalHeight = Math.max(200, maxHeight);
                    
                    console.log('[React] Updated modal dimensions:', {
                        received: { width: data.width, height: data.height },
                        calculated: { width: finalWidth, height: finalHeight }
                    });
                }

                // Handle iframe resize from Flutter
                if (data?.type === 'resize') {
                    const iframe = document.querySelector('.appointment-summary-modal iframe');
                    console.log('[React] Resize message received from Flutter:', data);
                    if (iframe) {
                        const newHeight = Math.max(data.height || 405, 310); // Min 300px
                        iframe.style.height = `${newHeight}px`;
                        console.log(`📏 [AppointmentSummary] Resized iframe to ${newHeight}px`);
                    }
                }

                // Handle appointment actions
                if (data?.type === 'appointment_action') {
                    console.log('[React] Appointment action received:', data.action);

                    switch (data.action) {
                        case 'close':
                        case 'accept':
                        case 'reject':
                        case 'cancel':
                            handleClose();
                            break;
                        default:
                            // Other actions already handled above
                            break;
                    }
                }
            } catch (error) {
                console.error('[React] Error parsing message from Flutter:', {
                    error: error.message,
                    eventData: event.data
                });
            }
        };

        window.addEventListener('message', handleFlutterMessage);

        // Iframe load handlers
        const iframe = iframeRef.current;
        if (iframe) {
            iframe.onload = () => {
                console.log('[React] ✅ Iframe loaded - waiting for flutter_ready signal');
                
                // Wait for Flutter ready signal
                initialSendTimerId = setTimeout(() => {
                    if (!hasDataBeenSentRef.current && !isFlutterReadyRef.current) {
                        console.log('[React] ⏰ Flutter ready signal not received, sending data as fallback...');
                        sendDataToFlutter();
                    }
                }, 1000);
            };

            iframe.onerror = (error) => {
                console.error('[React] ❌ Iframe load error:', error);
                setIsIframeLoading(false);
            };
        }

        // Cleanup
        return () => {
            window.removeEventListener('message', handleFlutterMessage);
            
            // Clear all timers
            if (fallbackTimerId) {
                clearTimeout(fallbackTimerId);
            }
            if (initialSendTimerId) {
                clearTimeout(initialSendTimerId);
            }
            
            if (iframe) {
                iframe.onload = null;
                iframe.onerror = null;
            }
        };
    }, [isAppointmentSummaryPopupOpen, appointmentSummaryDetails, authData, flutterOrigin, flutterIframeUrl]);

    return (
        <>
            <Modal
                isOpen={isAppointmentSummaryPopupOpen}
                toggle={handleClose}
                centered
                className='appointment-summary-modal'
            >
                <ModalBody>
                    {isIframeLoading && (
                        <div className="appointment-modal-spinner-overlay">
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Loading appointment...</span>
                            </div>
                        </div>
                    )}
                    
                    <iframe
                        ref={iframeRef}
                        src={flutterIframeUrl}

                        allowFullScreen
                        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-popups-to-escape-sandbox allow-storage-access-by-user-activation"
                        allow="fullscreen"
                        title="Appointment Summary Flutter"                        
                        onError={(e) => {
                            console.error('[React] Iframe error event:', {
                                error: e,
                                src: flutterIframeUrl
                            });
                        }}
                    /> 
                </ModalBody>
            </Modal>
        </>
    );
};

AppointmentPopupSummaryComponent.displayName = "AppointmentPopupSummary";
const AppointmentPopupSummary = memo(AppointmentPopupSummaryComponent); 
export default AppointmentPopupSummary;